import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FarmersService } from '../../farmers.service';
import { finalize, forkJoin, Subscription, take } from 'rxjs';
import { Farmer, FarmerRegions } from '../../farmers.model';
import { PaginatedResponse } from '../../../common/models/common.model';
import { ProductService } from '../../../products/product.service';
import { ProductNameList } from '../../../products/product.model';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-farmers',
  standalone: false,
  templateUrl: './farmers.component.html',
  styleUrl: './farmers.component.scss',
})
export class FarmersComponent implements OnInit, OnDestroy {
  isFarmersDataLoading: boolean = false;
  farmers: Farmer[] = [];
  states: string[] = [];
  districts: string[] = [];
  talukas: string[] = [];
  villages: string[] = [];
  products: ProductNameList[] = [];
  crops: { key: string; value: string }[] = [];

  selectedState: string | null = null;
  selectedDistrict: string | null = null;
  selectedTaluka: string | null = null;
  selectedVillage: string | null = null;
  selectedProduct: string | null = null;
  selectedCrops: string | null = null;
  protected searchTerm: string = '';

  // Whatsapp Message
  @ViewChild('whatsappModal') whatsappModal!: TemplateRef<any>;

  farmersList: {
    full_name: string;
    id: string;
    whatsapp_number: string;
    mobile_number: string;
  }[] = [];
  selectedFarmerIds: string[] = [];
  message: string = '';
  selectAll: boolean = false;
  isLoading: boolean = false;
  isSending: boolean = false;
  isExportingFarmers: boolean = false;
  private searchDebounceTimer!: any;
  private _exportSub?: Subscription;
  modalRef?: NgbModalRef;

  protected totalCount: number = 0;
  protected hasNextPage = false;
  protected hasPreviousPage = false;
  protected currentPage: number = 1;
  protected readonly pageSize = 10;

  constructor(
    private farmersService: FarmersService,
    private productService: ProductService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    this.getFarmerFiltersData();
    this.getFarmersData();
  }

  getFarmerFiltersData(): void {
    const productsList$ = this.productService.lighter_list();
    const regions$ = this.farmersService.get_regions();
    const cropsList$ = this.farmersService.crops_lighter_list();
    const farmersList$ = this.farmersService.farmers_lighter_list();

    forkJoin([productsList$, regions$, cropsList$, farmersList$]).subscribe({
      next: ([products, farmerRegions, crops, farmersList]: [
        ProductNameList[],
        FarmerRegions,
        {
          key: string;
          value: string;
        }[],
        {
          full_name: string;
          id: string;
          whatsapp_number: string;
          mobile_number: string;
        }[],
      ]) => {
        this.products = products;
        this.villages = farmerRegions.vilages;
        this.talukas = farmerRegions.talukas;
        this.districts = farmerRegions.districts;
        this.states = farmerRegions.states;
        this.crops = crops;
        this.farmersList = farmersList;
      },
    });
  }

  getFarmersData(): void {
    const q = {
      page: this.currentPage,
      vilage: this.selectedVillage,
      district: this.selectedDistrict,
      taluka: this.selectedTaluka,
      state: this.selectedState,
      product_id: this.selectedProduct,
      crops: this.selectedCrops,
      search: this.searchTerm,
    };

    this.isFarmersDataLoading = true;
    this.farmersService
      .get_farmers(q)
      .pipe(finalize(() => (this.isFarmersDataLoading = false)))
      .subscribe({
        next: (farmersResponse: PaginatedResponse<Farmer>) => {
          this.farmers = farmersResponse.results || [];
          this.totalCount = farmersResponse.count;
          this.hasNextPage = !!farmersResponse.next;
          this.hasPreviousPage = !!farmersResponse.previous;
        },
      });
  }

  onFilterChange(): void {
    this.currentPage = 1;

    this.getFarmersData();
  }

  protected onSearchKeyUp(): void {
    // Clear any existing timer
    clearTimeout(this.searchDebounceTimer);

    // Set a new debounce timer (500 ms)
    this.searchDebounceTimer = setTimeout(() => {
      this.currentPage = 1;
      this.getFarmersData();
    }, 500);
  }

  onCropsSelected(selected: string[]) {
    this.selectedCrops = selected.join(',');
    this.onFilterChange();
  }

  onExportFarmers(): void {
    this.isExportingFarmers = true;
    this._exportSub = this.farmersService
      .export()
      .pipe(finalize(() => (this.isExportingFarmers = false)))
      .subscribe({
        next: (blob: Blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'farmers.xlsx';
          a.click();
          URL.revokeObjectURL(url);
        },
      });
  }

  onSendMessage(): void {
    this.farmersService.farmers_lighter_list().subscribe({});
    this.modalRef = this.modalService.open(this.whatsappModal, {
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
    });
  }

  toggleSelectAll(): void {
    if (this.selectAll) {
      this.selectedFarmerIds = this.farmers.map(f => f.id);
    } else {
      this.selectedFarmerIds = [];
    }
  }

  onFarmerSelectionChange(): void {
    this.selectAll = this.selectedFarmerIds.length === this.farmers.length;
  }

  isFarmerSelected(farmerId: string): boolean {
    return this.selectedFarmerIds.includes(farmerId);
  }

  toggleFarmerSelection(farmerId: string): void {
    const index = this.selectedFarmerIds.indexOf(farmerId);
    if (index > -1) {
      this.selectedFarmerIds.splice(index, 1);
    } else {
      this.selectedFarmerIds.push(farmerId);
    }
    this.onFarmerSelectionChange();
  }

  sendWhatsAppMessage(): void {
    if (this.selectedFarmerIds.length === 0) {
      alert('Please select at least one farmer');
      return;
    }

    if (!this.message.trim()) {
      alert('Please enter a message');
      return;
    }

    const payload = {
      farmer_ids: this.selectedFarmerIds,
      message: this.message,
    };

    this.isSending = true;
    this.farmersService
      .send_message(payload)
      .pipe(finalize(() => (this.isSending = false)))
      .subscribe({
        next: response => {
          this.closeModal();
        },
        error: error => {
          alert('Failed to send message. Please try again.');
        },
      });
  }

  closeModal(): void {
    this.selectedFarmerIds = [];
    this.message = '';
    this.selectAll = false;
    this.modalRef?.close();
  }

  ngOnDestroy() {
    this._exportSub?.unsubscribe();
  }

  protected onPageChange(page: number): void {
    this.currentPage = page;
    this.getFarmersData();
  }

  /** The row's number in the whole result set, so page 2 starts at 11. */
  protected rowNumber(index: number): number {
    return (this.currentPage - 1) * this.pageSize + index + 1;
  }
}
